import { useState } from 'react';
import type { Deal, SCData, TabState, Touchpoint } from '../../types';
import { buildSCList, buildTouchpointSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
type DealViewMode = 'cards' | 'table' | 'tile' | 'gallery' | 'waterfall';

/* ─── SVG icons for the view switcher ──────────────────────────────────── */

function IconCards() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-275" data-mp="3m1bqndvxgq59">
      <rect x="1" y="1" width="6" height="6" rx="1" data-id="element-276" data-mp="3h30fmztwc9lg" />
      <rect x="9" y="1" width="6" height="6" rx="1" data-id="element-277" data-mp="3h04gx9a85gvy" />
      <rect x="1" y="9" width="6" height="6" rx="1" data-id="element-278" data-mp="3vq3cd4aeae7q" />
      <rect x="9" y="9" width="6" height="6" rx="1" data-id="element-279" data-mp="3vsxweuphxx0m" />
    </svg>;
}
function IconTable() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" data-id="element-280" data-mp="332fx84rw8fsh">
      <rect x="1" y="2" width="14" height="12" rx="1.5" data-id="element-281" data-mp="2y3csymjqmdlk" />
      <line x1="1" y1="5.5" x2="15" y2="5.5" data-id="element-282" data-mp="2y4xly7qelohb" />
      <line x1="1" y1="9" x2="15" y2="9" data-id="element-283" data-mp="3c2vzjawwhgvr" />
      <line x1="6" y1="5.5" x2="6" y2="14" data-id="element-284" data-mp="3c3xcxm1e2fgn" />
    </svg>;
}
function IconTile() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-285" data-mp="380r7ggm3osb8">
      <rect x="1" y="1" width="4" height="4" rx="0.5" data-id="element-286" data-mp="rezqyc2wedaj" />
      <rect x="6" y="1" width="4" height="4" rx="0.5" data-id="element-287" data-mp="na9w8qjz74r7" />
      <rect x="11" y="1" width="4" height="4" rx="0.5" data-id="element-288" data-mp="ndo2i9vrfy3x" />
      <rect x="1" y="6" width="4" height="4" rx="0.5" data-id="element-289" data-mp="3g50crjd5zuun" />
      <rect x="6" y="6" width="4" height="4" rx="0.5" data-id="element-290" data-mp="3l4motph9b51v" />
      <rect x="11" y="6" width="4" height="4" rx="0.5" data-id="element-291" data-mp="3l31u9zuxzm40" />
      <rect x="1" y="11" width="4" height="4" rx="0.5" data-id="element-292" data-mp="3q3g15ekt4q0g" />
      <rect x="6" y="11" width="4" height="4" rx="0.5" data-id="element-293" data-mp="3q1v6zq2en934" />
      <rect x="11" y="11" width="4" height="4" rx="0.5" data-id="element-294" data-mp="3u4jlad9w187h" />
    </svg>;
}
function IconGallery() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-295" data-mp="2xgljsqjae6r7">
      <rect x="1" y="1" width="6" height="7" rx="1" data-id="element-296" data-mp="32caqddtq4nx0" />
      <rect x="9" y="1" width="6" height="7" rx="1" data-id="element-297" data-mp="36exlpoplsqyy" />
      <rect x="1" y="10" width="14" height="5" rx="1" data-id="element-298" data-mp="36e5n050pmd4g" />
    </svg>;
}
function IconWaterfall() {
  return <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-299" data-mp="2eossd0jv8tll">
      <line x1="2" y1="1" x2="2" y2="15" data-id="element-300" data-mp="2ipmfvstec6dy" />
      <line x1="6" y1="1" x2="6" y2="15" data-id="element-301" data-mp="2iobd9wbot4of" />
      <line x1="10" y1="1" x2="10" y2="15" data-id="element-302" data-mp="2b7etn2f6kkmz" />
      <line x1="14" y1="1" x2="14" y2="15" data-id="element-303" data-mp="2b63rt85apmyi" />
      <rect x="1" y="3" width="3" height="2" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-304" data-mp="2681zdwlzcbad" />
      <rect x="5" y="6" width="3" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-305" data-mp="2266w4mkhnnty" />
      <rect x="9" y="2" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-306" data-mp="2281gnzruldxi" />
      <rect x="13" y="8" width="2" height="3" rx="0.5" fill="currentColor" opacity="0.3" data-id="element-307" data-mp="1x9888lrf4d47" />
    </svg>;
}
const VIEW_OPTIONS: {
  mode: DealViewMode;
  label: string;
  icon: React.FC;
}[] = [{
  mode: 'cards',
  label: 'Cards',
  icon: IconCards
}, {
  mode: 'table',
  label: 'Table',
  icon: IconTable
}, {
  mode: 'tile',
  label: 'Tile',
  icon: IconTile
}, {
  mode: 'gallery',
  label: 'Gallery',
  icon: IconGallery
}, {
  mode: 'waterfall',
  label: 'Waterfall',
  icon: IconWaterfall
}];

/* ─── Shared request-name link with icon ───────────────────────────────── */

const LINK_STYLE: React.CSSProperties = {
  color: '#1d4ed8',
  textDecoration: 'none'
};
function RequestLink({
  d,
  maxLen = 35
}: {
  d: Deal;
  maxLen?: number;
}) {
  return <a href={getURL(d)} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} title={d.requestName} data-id="element-308" data-mp="39ir8m6txa8g1">
      <svg viewBox="0 0 12 12" fill="none" stroke="#1d4ed8" strokeWidth="1.4" strokeLinecap="round" style={{
      display: 'inline-block',
      width: 9,
      height: 9,
      marginRight: 3,
      verticalAlign: 'middle',
      position: 'relative',
      top: -1
    }} data-id="element-309" data-mp="2uud47rguybxd">
        <path d="M5 1.5H3.5A2 2 0 0 0 1.5 3.5v5A2 2 0 0 0 3.5 10.5h5a2 2 0 0 0 2-2V8.5" data-id="element-310" data-mp="2pvterh322i09" />
        <path d="M7.5 1.5h3v3" data-id="element-311" data-mp="2ptgtov203riz" />
        <path d="M5.5 6.5 10.5 1.5" data-id="element-312" data-mp="2bwttmjer4l8u" />
      </svg>
      {d.requestName.substring(0, maxLen)}
    </a>;
}

/* ─── View switcher bar ────────────────────────────────────────────────── */

function ViewSwitcher({
  active,
  onChange
}: {
  active: DealViewMode;
  onChange: (m: DealViewMode) => void;
}) {
  return <div className="view-switcher" data-id="element-313" data-mp="f2p9m2pbgvhq">
      {VIEW_OPTIONS.map(opt => <button key={opt.mode} className={active === opt.mode ? 'active' : ''} onClick={() => onChange(opt.mode)} title={opt.label} data-id="element-314" data-mp="1zlj9x9sn15we">
          <opt.icon data-id="element-315" data-mp="1ukvd27hq9uu9" />
          {opt.label}
        </button>)}
    </div>;
}

/* ─── Age bucket helper for waterfall ──────────────────────────────────── */

function ageBucket(age: number | null): string {
  if (age == null) return 'Unknown';
  if (age <= 7) return '0–7 days';
  if (age <= 14) return '8–14 days';
  if (age <= 30) return '15–30 days';
  return '30+ days';
}
const AGE_BUCKETS = ['0–7 days', '8–14 days', '15–30 days', '30+ days'];
function groupByAgeBucket(deals: Deal[]): Record<string, Deal[]> {
  const groups: Record<string, Deal[]> = {};
  for (const b of AGE_BUCKETS) groups[b] = [];
  for (const d of deals) {
    const b = ageBucket(d.age);
    if (!groups[b]) groups[b] = [];
    groups[b].push(d);
  }
  return groups;
}

/* ─── Display-only age for in-progress deals ──────────────────────────── */

/* ─── Individual view renderers ────────────────────────────────────────── */

function TableView({
  deals
}: {
  deals: Deal[];
}) {
  return <table className="deal-table" data-id="element-316" data-mp="1mp60vtugr31v">
      <thead data-id="element-317" data-mp="1rmfxpuk6mgzq">
        <tr data-id="element-318" data-mp="1rlwr39zzff10">
          <th style={{
          width: '9%'
        }} data-id="element-319" data-mp="1rl4ym81olxey">ID</th>
          <th style={{
          width: '16%'
        }} data-id="element-320" data-mp="1roixh6b4zohn">Account</th>
          <th style={{
          width: '18%'
        }} data-id="element-321" data-mp="1wmanfus810j7">Request</th>
          <th style={{
          width: '8%'
        }} data-id="element-322" data-mp="1wiwpcyqlbdhk">Age</th>
          <th style={{
          width: '8%'
        }} data-id="element-323" data-mp="21jay6j00mrgn">Date</th>
          <th style={{
          width: '9%'
        }} data-id="element-324" data-mp="21ijarvgko01g">GNARR</th>
          <th style={{
          width: '9%'
        }} data-id="element-325" data-mp="25kw57npvcbix">Close Q</th>
          <th style={{
          width: '13%'
        }} data-id="element-326" data-mp="25jl0nlolmzqr">Offering</th>
          <th style={{
          width: '10%'
        }} data-id="element-327" data-mp="2adp9yblc7jv0">Sub Region</th>
        </tr>
      </thead>
      <tbody data-id="element-328" data-mp="2fca9s9wog1ix">
        {deals.map(d => <tr key={d.requestID} data-id="element-329" data-mp="2faz76deywzte">
            <td style={{
          color: '#8892a0',
          fontSize: 9
        }} data-id="element-330" data-mp="2kau00miazzh3">{d.id}</td>
            <td title={d.account} data-id="element-331" data-mp="2kcoilu63rfi0">{d.account.substring(0, 24)}</td>
            <td title={d.requestName} data-id="element-332" data-mp="2pazusbjve23e">
              <RequestLink d={d} maxLen={30} data-id="element-333" data-mp="2p7u62xf5l287" />
            </td>
            <td className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
          fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
        }} data-id="element-334" data-mp="dnmnbfi3n67h">
              {formatAge(d.age)}
            </td>
            <td style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-335" data-mp="3qhwv3h80okv">{d.entry}</td>
            <td data-id="element-336" data-mp="3uw01argzkg7s">{fmtG(d.gnarr)}</td>
            <td data-id="element-337" data-mp="3ux2vb89luyrx">{d.close_q}</td>
            <td data-id="element-338" data-mp="wdm3tdzvjcza"><span className="badge b-blue" style={{
            fontSize: 8
          }} data-id="element-339" data-mp="wdcdtqetx5tl">{d.offering.substring(0, 18)}</span></td>
            <td data-id="element-340" data-mp="rf13lekmgtau"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-341" data-mp="rf155j09t1cy">{d.sub_region}</span></td>
          </tr>)}
      </tbody>
    </table>;
}
function TileView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-tile-grid" data-id="element-342" data-mp="hnexfwn55key">
      {deals.map(d => <div key={d.requestID} className="deal-tile" title={`${d.account}\n${d.requestName}\nAge: ${formatAge(d.age)}\nGNARR: ${fmtG(d.gnarr)}\nDate: ${d.entry}`} data-id="element-343" data-mp="1f43uze5260ij">
          <div className="dt-id" data-id="element-344" data-mp="1aar7x8g121rw">{d.id}</div>
          <div className="dt-acct" data-id="element-345" data-mp="ip0wlbrwiz4v">{d.account.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-346" data-mp="nfzeugwga7ar">
            <RequestLink d={d} maxLen={16} data-id="element-347" data-mp="nhakynddbr51" />
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-348" data-mp="9pdwquetm1dx">{d.entry}</div>
          <span className={`dt-age ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-349" data-mp="en5wgalpirso">
            {formatAge(d.age)}
          </span>
        </div>)}
    </div>;
}
function GalleryView({
  deals
}: {
  deals: Deal[];
}) {
  return <div className="deal-gallery" data-id="element-350" data-mp="sfcczxsrv0sn">
      {deals.map(d => <div key={d.requestID} className="deal-gallery-card" data-id="element-351" data-mp="seu9qh7ozmb8">
          <div className="dg-id" data-id="element-352" data-mp="xddtcaytcm0f">{d.id}</div>
          <div className="dg-title" data-id="element-353" data-mp="xg9sg2medgqg">{d.account}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-354" data-mp="3d51nxzqcu2g2">
            <RequestLink d={d} maxLen={40} data-id="element-355" data-mp="3d7nupx5f8dx8" />
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-356" data-mp="3i5z82huxd6k7">{d.entry}</div>
          <div className="dg-meta" data-id="element-357" data-mp="3ajk8f621zzxu">
            <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-358" data-mp="3amzmq6nwl4y7">{formatAge(d.age)}</span>
            <span className="badge b-blue" data-id="element-359" data-mp="3jka1w33ude8e">{fmtG(d.gnarr)}</span>
            <span className="badge b-purple" style={{
          fontSize: 8
        }} data-id="element-360" data-mp="3emqeodp61r4b">{d.close_q}</span>
            {isAged(d) && <span className="badge b-red" data-id="element-361" data-mp="2qwe753xu1we9">⚠ aged</span>}
          </div>
          <div className="dg-footer" data-id="element-362" data-mp="2qujjviz39s70">
            {d.sub_region} · {d.offering.substring(0, 26)}
          </div>
        </div>)}
    </div>;
}
function WaterfallView({
  deals
}: {
  deals: Deal[];
}) {
  const groups = groupByAgeBucket(deals);
  return <div className="deal-waterfall" data-id="element-363" data-mp="2vv7ai3jeo70p">
      {AGE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-364" data-mp="2841v1phdngqx">
            <div className="deal-waterfall-lane-header" data-id="element-365" data-mp="236jx0u4welxp">
              <span data-id="element-366" data-mp="9m7e7mgzvqd1">{bucket}</span>
              <span className="lane-count" data-id="element-367" data-mp="9lebcwl5pl06">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-368" data-mp="4n34muq5rqm6">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-369" data-mp="3vt4gv6lv0q9e">none</div>}
              {items.map(d => <div key={d.requestID} className="deal-waterfall-card" data-id="element-370" data-mp="3mspmlhqx40tr">
                  <div className="dw-id" data-id="element-371" data-mp="3mv28u73pkxcm">{d.id}</div>
                  <div className="dw-acct" title={d.account} data-id="element-372" data-mp="3i17vdlep5elz">{d.account.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-373" data-mp="3d2edzhwah3gj">
                    <RequestLink d={d} maxLen={20} data-id="element-374" data-mp="384lc3120qvm2" />
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-375" data-mp="3828krxadf8wc">{d.entry}</div>
                  <div className="dw-tags" data-id="element-376" data-mp="344avkuglomsy">
                    <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} style={{
                fontSize: 8
              }} data-id="element-377" data-mp="2z353myurl3kh">{formatAge(d.age)}</span>
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-378" data-mp="1jjnvms132sfs">{fmtG(d.gnarr)}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Dispatch view ────────────────────────────────────────────────────── */

function DealExpandedView({
  deals,
  mode
}: {
  deals: Deal[];
  mode: DealViewMode;
}) {
  const [dSearchId, setDSearchId] = useState('');
  const [dSearchAccount, setDSearchAccount] = useState('');
  const [dSearchRequest, setDSearchRequest] = useState('');
  const [dAgeMin, setDAgeMin] = useState('');
  const [dAgeMax, setDAgeMax] = useState('');
  const [dEntryFrom, setDEntryFrom] = useState('');
  const [dEntryTo, setDEntryTo] = useState('');
  const [dGnarrMin, setDGnarrMin] = useState('');
  const [dGnarrMax, setDGnarrMax] = useState('');
  const [dCloseQFilter, setDCloseQFilter] = useState<string[]>([]);
  const [dCloseQDropdownOpen, setDCloseQDropdownOpen] = useState(false);
  const [dOfferingFilter, setDOfferingFilter] = useState<string[]>([]);
  const [dOfferingDropdownOpen, setDOfferingDropdownOpen] = useState(false);
  const [dSubFilter, setDSubFilter] = useState<string[]>([]);
  const [dSubDropdownOpen, setDSubDropdownOpen] = useState(false);
  const dCloseQs = [...new Set(deals.map(d => d.close_q).filter(Boolean))].sort();
  const dOfferings = [...new Set(deals.map(d => d.offering).filter(Boolean))].sort();
  const dSubs = [...new Set(deals.map(d => d.sub_region).filter(Boolean))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = deals.filter(d => {
    if (dSearchId && !d.id.toLowerCase().includes(dSearchId.toLowerCase())) return false;
    if (dSearchAccount && !d.account.toLowerCase().includes(dSearchAccount.toLowerCase())) return false;
    if (dSearchRequest && !d.requestName.toLowerCase().includes(dSearchRequest.toLowerCase())) return false;
    if (dAgeMin || dAgeMax) {
      const age = d.age;
      if (age === null) return false;
      if (dAgeMin && age < Number(dAgeMin)) return false;
      if (dAgeMax && age > Number(dAgeMax)) return false;
    }
    if (dEntryFrom || dEntryTo) {
      const dt = parseEntry(d.entry);
      if (dt) {
        if (dEntryFrom) {
          const f = new Date(dEntryFrom);
          if (dt < f) return false;
        }
        if (dEntryTo) {
          const t = new Date(dEntryTo);
          if (dt > t) return false;
        }
      }
    }
    if (dGnarrMin && d.gnarr < Number(dGnarrMin)) return false;
    if (dGnarrMax && d.gnarr > Number(dGnarrMax)) return false;
    if (dCloseQFilter.length > 0 && !dCloseQFilter.includes(d.close_q)) return false;
    if (dOfferingFilter.length > 0 && !dOfferingFilter.includes(d.offering)) return false;
    if (dSubFilter.length > 0 && !dSubFilter.includes(d.sub_region)) return false;
    return true;
  });
  if (deals.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '8px 0',
      fontStyle: 'italic'
    }} data-id="element-379" data-mp="23at7x5820g5q">No in-progress deals.</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const numStyle = {
    fontSize: 10,
    padding: '4px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 60,
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 4
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-380" data-mp="3v79t75akwccr">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-381" data-mp="418f0nvbh2mw">
        {selected.length > 0 ? `${selected.length} selected` : `${items[0] === dCloseQs[0] ? 'Close Q' : items[0] === dOfferings[0] ? 'Offering' : 'Sub'} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-382" data-mp="dqrwbp3dsofn">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-383" data-mp="iqeco7eiakso">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-384" data-mp="2euu6bifey0rp">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-385" data-mp="2nt64c5im00u6" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.entry) - toComparable(a.entry));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-386" data-mp="1d4goy3pu7oa1">
      <input type="text" placeholder="ID" value={dSearchId} onChange={e => setDSearchId(e.target.value)} style={inputStyle} data-id="element-387" data-mp="130iht6g7idv1" />
      <input type="text" placeholder="Account" value={dSearchAccount} onChange={e => setDSearchAccount(e.target.value)} style={inputStyle} data-id="element-388" data-mp="170sj45t20g3k" />
      <input type="text" placeholder="Request" value={dSearchRequest} onChange={e => setDSearchRequest(e.target.value)} style={{
      ...inputStyle,
      width: 130
    }} data-id="element-389" data-mp="t8cnvr6e9md7" />
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-390" data-mp="jb7xtg9fh6r4">
        <span data-id="element-391" data-mp="oc5grv29i4to">Age</span>
        <input type="number" placeholder="min" value={dAgeMin} onChange={e => setDAgeMin(e.target.value)} style={numStyle} data-id="element-392" data-mp="ocnnxmqgq3gd" />
        <span data-id="element-393" data-mp="fao3ekg8oh8o">–</span>
        <input type="number" placeholder="max" value={dAgeMax} onChange={e => setDAgeMax(e.target.value)} style={numStyle} data-id="element-394" data-mp="fa6053v5t2r9" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-395" data-mp="24q5eczdmxidr">
        <span data-id="element-396" data-mp="29o6xfkb29hp8">Entry</span>
        <input type="date" value={dEntryFrom} onChange={e => setDEntryFrom(e.target.value)} style={dateStyle} data-id="element-397" data-mp="29mcn0zxf17ze" />
        <span data-id="element-398" data-mp="3da1fjgzfr0p5">–</span>
        <input type="date" value={dEntryTo} onChange={e => setDEntryTo(e.target.value)} style={dateStyle} data-id="element-399" data-mp="3d9jem71tvyaw" />
      </div>
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-400" data-mp="38i9qxbzub51n">
        <span data-id="element-401" data-mp="34gy6dkvtpa0b">GNARR</span>
        <input type="number" placeholder="min" value={dGnarrMin} onChange={e => setDGnarrMin(e.target.value)} style={numStyle} data-id="element-402" data-mp="34hhgiff8eu3s" />
        <span data-id="element-403" data-mp="2ujju7xgxn7dt">–</span>
        <input type="number" placeholder="max" value={dGnarrMax} onChange={e => setDGnarrMax(e.target.value)} style={numStyle} data-id="element-404" data-mp="2ujaausqe7yh3" />
      </div>
      {renderDropdown(dCloseQs, dCloseQFilter, setDCloseQFilter, dCloseQDropdownOpen, setDCloseQDropdownOpen)}
      {renderDropdown(dOfferings, dOfferingFilter, setDOfferingFilter, dOfferingDropdownOpen, setDOfferingDropdownOpen)}
      {renderDropdown(dSubs, dSubFilter, setDSubFilter, dSubDropdownOpen, setDSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-405" data-mp="21oc8icioci0d">{filtered.length} of {deals.length} deals</div>
      <DealExpandedViewInner deals={sorted} mode={mode} data-id="element-406" data-mp="26mdq0t0gc99q" />
    </>;
}
function DealExpandedViewInner({
  deals,
  mode
}: {
  deals: Deal[];
  mode: DealViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TableView deals={deals} data-id="element-407" data-mp="2ffqoeahac337" />;
    case 'tile':
      return <TileView deals={deals} data-id="element-408" data-mp="1ir19mt0yfqlg" />;
    case 'gallery':
      return <GalleryView deals={deals} data-id="element-409" data-mp="1ism2me7mf1h7" />;
    case 'waterfall':
      return <WaterfallView deals={deals} data-id="element-410" data-mp="1nq5fpasl187k" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-411" data-mp="1rqo7x5ye0gaa">
          {deals.map(d => <div key={d.requestID} className="deal-chip" data-id="element-412" data-mp="1wq0xhygn81gk">
              <div className="dc-id" data-id="element-413" data-mp="1wrlqvkr81ecu">{d.id}</div>
              <div className="dc-name" data-id="element-414" data-mp="336cw20ndnlco">{d.account.substring(0, 35)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 3
          }} data-id="element-415" data-mp="338gzyila0kcy"><RequestLink d={d} data-id="element-416" data-mp="384efimoptmoo" /></div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-417" data-mp="383cv3rlt7nua">{d.entry}</div>
              <div className="dc-tags" data-id="element-418" data-mp="39ldryuw3c47f">
            <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-419" data-mp="3el0574bx5kg8">{formatAge(d.age)}</span>
                <span className="badge b-blue" data-id="element-420" data-mp="3jci7bvpohr2s">{fmtG(d.gnarr)}</span>
                <span className="badge b-purple" style={{
              fontSize: 8
            }} data-id="element-421" data-mp="3o9rsv09y5ilc">{d.close_q}</span>
                {isAged(d) && <span className="badge b-red" data-id="element-422" data-mp="2qo46l877ggnj">⚠ aged</span>}
              </div>
              <div style={{
            fontSize: 9,
            color: '#8892a0',
            marginTop: 3
          }} data-id="element-423" data-mp="2vlmb8me6w9pq">
                {d.sub_region} · {d.offering.substring(0, 26)}
              </div>
            </div>)}
        </div>;
  }
}

/* ─── SC Matrix (unchanged) ───────────────────────────────────────────── */

function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    const bkts = bucketProducts(d.products || '');
    for (const scName of splitSCNames(d.sc)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + d.id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-424" data-mp="vdm1ptq0w2h4">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-425" data-mp="vdcdobojg5e2">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-426" data-mp="1o1htql4nd67k">
        <tr data-id="element-427" data-mp="1o21ee9lom8pa">
          <th data-id="element-428" data-mp="1o0q925cl8sw2">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-429" data-mp="196bqg5wxrlhx">{c}</th>)}
          <th data-id="element-430" data-mp="1950l41nue5op">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-431" data-mp="1e4d9wry9xmtx">
        {scRows.map(row => <tr key={row.full} data-id="element-432" data-mp="1e323slhcw2zn">
            <td title={row.full} data-id="element-433" data-mp="1e6g9o3p8aubu">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-434" data-mp="3txvptw500xe6">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-435" data-mp="2q9gpnzig2px">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-436" data-mp="2gwzbobejmyz5">—</span>}
              </td>)}
            <td data-id="element-437" data-mp="2byeigvxplfk7"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-438" data-mp="2byegwri297i3">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-439" data-mp="2pxdxrczc0i3p">
        <tr data-id="element-440" data-mp="2pvtc6cuzw9hz">
          <td data-id="element-441" data-mp="2pvkywjuyn269">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-442" data-mp="2lxn8jdpgea1a"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-443" data-mp="2lwu2keybjok7">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-444" data-mp="1y6zmzpmm7lw9"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-445" data-mp="1y4n7rk88rpmw">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}

/* ─── Touchpoint expanded view ──────────────────────────────────────────── */

function TouchpointExpandedView({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: DealViewMode;
}) {
  const [tSearchId, setTSearchId] = useState('');
  const [tSearchName, setTSearchName] = useState('');
  const [tSearchAccount, setTSearchAccount] = useState('');
  const [tSearchSc, setTSearchSc] = useState('');
  const [tSearchComments, setTSearchComments] = useState('');
  const [tTypeFilter, setTypeFilter] = useState<string[]>([]);
  const [tTypeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [tEntryFrom, setTEntryFrom] = useState('');
  const [tEntryTo, setTEntryTo] = useState('');
  const [tCloseQFilter, setTCloseQFilter] = useState<string[]>([]);
  const [tCloseQDropdownOpen, setTCloseQDropdownOpen] = useState(false);
  const [tSubFilter, setTSubFilter] = useState<string[]>([]);
  const [tSubDropdownOpen, setTSubDropdownOpen] = useState(false);
  const tTypes = [...new Set(touchpoints.map(t => t.touchpointType).filter(Boolean))].sort();
  const tCloseQs = [...new Set(touchpoints.map(t => t.close_q).filter(Boolean))].sort();
  const tSubs = [...new Set(touchpoints.map(t => t.sub_region).filter(Boolean))].sort();
  const parseEntry = (entry: string): Date | null => {
    if (!entry) return null;
    const p = entry.split('/');
    if (p.length !== 3) return null;
    const [dd, mm, yy] = p.map(Number);
    return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd);
  };
  const filtered = touchpoints.filter(t => {
    if (tSearchId && !t.id.toLowerCase().includes(tSearchId.toLowerCase())) return false;
    if (tSearchName && !t.name.toLowerCase().includes(tSearchName.toLowerCase())) return false;
    if (tSearchAccount && !t.account.toLowerCase().includes(tSearchAccount.toLowerCase())) return false;
    if (tSearchSc && !t.sc.toLowerCase().includes(tSearchSc.toLowerCase())) return false;
    if (tSearchComments && !(t.solutionComments || '').toLowerCase().includes(tSearchComments.toLowerCase())) return false;
    if (tTypeFilter.length > 0 && !tTypeFilter.includes(t.touchpointType)) return false;
    if (tEntryFrom || tEntryTo) {
      const dt = parseEntry(t.entry);
      if (dt) {
        if (tEntryFrom) {
          const f = new Date(tEntryFrom);
          if (dt < f) return false;
        }
        if (tEntryTo) {
          const te = new Date(tEntryTo);
          if (dt > te) return false;
        }
      }
    }
    if (tCloseQFilter.length > 0 && !tCloseQFilter.includes(t.close_q)) return false;
    if (tSubFilter.length > 0 && !tSubFilter.includes(t.sub_region)) return false;
    return true;
  });
  if (touchpoints.length === 0) {
    return <div style={{
      fontSize: 10,
      color: '#8892a0',
      padding: '12px 4px',
      textAlign: 'center'
    }} data-id="element-446" data-mp="1pq0o2lnqmepo">No touchpoints for this SC</div>;
  }
  const inputStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    width: 110,
    color: '#344054',
    background: '#fff'
  };
  const dateStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff'
  };
  const btnStyle = {
    fontSize: 10,
    padding: '4px 8px',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    outline: 'none',
    color: '#344054',
    background: '#fff',
    cursor: 'pointer' as const,
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: 4
  };
  const ddStyle = {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    marginTop: 4,
    zIndex: 50,
    background: '#fff',
    border: '1px solid #d0d5dd',
    borderRadius: 5,
    boxShadow: '0 2px 6px rgba(0,0,0,.12)',
    minWidth: 140,
    padding: '4px 0'
  };
  const renderDropdown = (label: string, items: string[], selected: string[], toggle: (v: string[]) => void, open: boolean, setOpen: (v: boolean) => void) => <div style={{
    position: 'relative'
  }} data-id="element-447" data-mp="nglqtuo66t4h">
      <button style={btnStyle} onClick={() => setOpen(!open)} data-id="element-448" data-mp="11ds459q4jzmb">
        {selected.length > 0 ? `${label} (${selected.length})` : `${label} ▾`}
      </button>
      {open && <div style={ddStyle} data-id="element-449" data-mp="1b43lpz2qe7bo">
          <div onClick={() => toggle(selected.length === items.length ? [] : [...items])} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        color: '#667085',
        borderBottom: '1px solid #eaecf0'
      }} data-id="element-450" data-mp="1b4d3x0hjba6t">
            {selected.length === items.length ? 'Deselect all' : 'Select all'}
          </div>
          {items.map(item => <label key={item} style={{
        padding: '4px 10px',
        fontSize: 10,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }} onClick={e => e.stopPropagation()} data-id="element-451" data-mp="1g13itkhlrzqn">
              <input type="checkbox" checked={selected.includes(item)} onChange={() => toggle(selected.includes(item) ? selected.filter(x => x !== item) : [...selected, item])} data-id="element-452" data-mp="5o420qsc2y4r" />
              {item}
            </label>)}
        </div>}
    </div>;
  const toComparable = (entry: string) => {
    if (!entry) return 0;
    const [dd, mm, yy] = entry.split('/');
    return +yy * 10000 + +mm * 100 + +dd;
  };
  const sorted = [...filtered].sort((a, b) => toComparable(b.entry) - toComparable(a.entry));
  const filterBar = <div style={{
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap'
  }} data-id="element-453" data-mp="3svptl5fff1hq">
      <input type="text" placeholder="ID" value={tSearchId} onChange={e => setTSearchId(e.target.value)} style={inputStyle} data-id="element-454" data-mp="2vehdc501bsz2" />
      <input type="text" placeholder="Name" value={tSearchName} onChange={e => setTSearchName(e.target.value)} style={inputStyle} data-id="element-455" data-mp="30ciswfy95a5s" />
      <input type="text" placeholder="Account" value={tSearchAccount} onChange={e => setTSearchAccount(e.target.value)} style={inputStyle} data-id="element-456" data-mp="1dhx6huvw2j9s" />
      <input type="text" placeholder="SC" value={tSearchSc} onChange={e => setTSearchSc(e.target.value)} style={inputStyle} data-id="element-457" data-mp="18h9hfer80ci7" />
      <input type="text" placeholder="Comments" value={tSearchComments} onChange={e => setTSearchComments(e.target.value)} style={{
      ...inputStyle,
      width: 120
    }} data-id="element-458" data-mp="13lker2jwmfho" />
      {renderDropdown('Type', tTypes, tTypeFilter, setTypeFilter, tTypeDropdownOpen, setTypeDropdownOpen)}
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      fontSize: 10,
      color: '#667085'
    }} data-id="element-459" data-mp="psuscx0gr8k1">
        <span data-id="element-460" data-mp="ku9omnm066qu">Entry</span>
        <input type="date" value={tEntryFrom} onChange={e => setTEntryFrom(e.target.value)} style={dateStyle} data-id="element-461" data-mp="kqvntjszm5li" />
        <span data-id="element-462" data-mp="fvq1wkzl4qxf">–</span>
        <input type="date" value={tEntryTo} onChange={e => setTEntryTo(e.target.value)} style={dateStyle} data-id="element-463" data-mp="av2avzbcwa37" />
      </div>
      {renderDropdown('Close Q', tCloseQs, tCloseQFilter, setTCloseQFilter, tCloseQDropdownOpen, setTCloseQDropdownOpen)}
      {renderDropdown('Sub', tSubs, tSubFilter, setTSubFilter, tSubDropdownOpen, setTSubDropdownOpen)}
    </div>;
  return <>
      {filterBar}
      <div style={{
      fontSize: 9,
      color: '#8892a0',
      marginBottom: 4
    }} data-id="element-464" data-mp="3kxq76o15mx7p">{sorted.length} of {touchpoints.length} touchpoints</div>
      <TouchpointExpandedViewInner touchpoints={sorted} mode={mode} data-id="element-465" data-mp="2x8xfx4vh9djf" />
    </>;
}
function TouchpointExpandedViewInner({
  touchpoints,
  mode
}: {
  touchpoints: Touchpoint[];
  mode: DealViewMode;
}) {
  switch (mode) {
    case 'table':
      return <TouchpointTableView touchpoints={touchpoints} data-id="element-466" data-mp="36zs5oj7xmv9m" />;
    case 'tile':
      return <TouchpointTileView touchpoints={touchpoints} data-id="element-467" data-mp="325fko4qt5bvr" />;
    case 'gallery':
      return <TouchpointGalleryView touchpoints={touchpoints} data-id="element-468" data-mp="2egcsw42gm2nj" />;
    case 'waterfall':
      return <TouchpointWaterfallView touchpoints={touchpoints} data-id="element-469" data-mp="29iusx36f6xro" />;
    case 'cards':
    default:
      return <div className="deal-grid" data-id="element-470" data-mp="p00opkzza37q">
          {touchpoints.map(t => <div key={t.task_id} className="deal-chip" title={`${t.account}\n${t.name}\nType: ${t.touchpointType || '—'}\nEntry: ${t.entry}`} data-id="element-471" data-mp="tqzbn3fh1zjy">
              <div style={{
            fontSize: 9,
            color: '#8892a0'
          }} data-id="element-472" data-mp="g0vrci05kfx5">{t.id}</div>
              <div style={{
            fontSize: 10,
            fontWeight: 600,
            marginTop: 2
          }} data-id="element-473" data-mp="ky5nshlylruh">{t.account.substring(0, 16)}</div>
              <div style={{
            fontSize: 9,
            marginTop: 2
          }} data-id="element-474" data-mp="18nzzgvujlad5">
                <span title={t.name} data-id="element-475" data-mp="18mf4j54bfper">{t.name.substring(0, 20)}{t.name.length > 20 ? '…' : ''}</span>
              </div>
              <div style={{
            display: 'flex',
            gap: 4,
            marginTop: 4,
            flexWrap: 'wrap'
          }} data-id="element-476" data-mp="yqc3y3p43ytx">
                {t.touchpointType && <span className="badge b-blue" style={{
              fontSize: 8
            }} data-id="element-477" data-mp="13ondedbhw7bm">{t.touchpointType}</span>}
                <span style={{
              fontSize: 8,
              color: '#667085'
            }} data-id="element-478" data-mp="3jfrsst4rtdgx">{t.entry}</span>
              </div>
              <div style={{
            fontSize: 8,
            color: '#667085',
            marginTop: 2
          }} data-id="element-479" data-mp="3ofxezts9u7rm">{t.sub_region} · {t.close_q}</div>
            </div>)}
        </div>;
  }
}

/* ─── Touchpoint view renderers ──────────────────────────────────────────── */

function TouchpointTableView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const tpColumns: SortableColumn<Touchpoint>[] = [{
    key: 'id',
    label: 'ID',
    width: '10%',
    sortValue: t => t.id,
    render: t => <span style={{
      color: '#8892a0',
      fontSize: 9
    }} data-id="element-480" data-mp="2qjsbhxcx0qd7">{t.id}</span>
  }, {
    key: 'name',
    label: 'Name',
    width: '16%',
    sortValue: t => t.name,
    render: t => <span title={t.name} data-id="element-481" data-mp="2gmno5u7c2out">{t.name.substring(0, 22)}</span>
  }, {
    key: 'account',
    label: 'Account',
    width: '14%',
    sortValue: t => t.account,
    render: t => <span title={t.account} data-id="element-482" data-mp="2cnyas1map7b6">{t.account.substring(0, 18)}</span>
  }, {
    key: 'sc',
    label: 'SC',
    width: '10%',
    sortValue: t => t.sc,
    render: t => <span data-id="element-483" data-mp="atpd02nc70xj">{t.sc}</span>
  }, {
    key: 'touchpointType',
    label: 'Type',
    width: '12%',
    sortValue: t => t.touchpointType,
    render: t => t.touchpointType ? <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-484" data-mp="5tt3xozs8jb4">{t.touchpointType}</span> : <span data-id="element-485" data-mp="dd2hzvj8h66i">—</span>
  }, {
    key: 'entry',
    label: 'Entry',
    width: '10%',
    sortValue: t => t.entry,
    render: t => <span style={{
      fontSize: 9,
      color: '#667085'
    }} data-id="element-486" data-mp="ibwz8t1jymoi">{t.entry}</span>
  }, {
    key: 'solutionComments',
    label: 'Comments',
    width: '18%',
    sortValue: t => t.solutionComments,
    render: t => <span title={t.solutionComments} data-id="element-487" data-mp="s65nv5ngpvhe">{t.solutionComments ? t.solutionComments.substring(0, 30) + (t.solutionComments.length > 30 ? '…' : '') : '—'}</span>
  }];
  return <SortableTable columns={tpColumns} data={touchpoints} rowKey={t => t.task_id} data-id="element-488" data-mp="3vlupfywjakac" />;
}
function TouchpointTileView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-tile-grid" data-id="element-489" data-mp="8gljc7eer9f6">
      {touchpoints.map(t => <div key={t.task_id} className="deal-tile" title={`${t.account}\n${t.name}\nType: ${t.touchpointType || '—'}\nEntry: ${t.entry}`} data-id="element-490" data-mp="8dr02j74runc">
          <div className="dt-id" data-id="element-491" data-mp="1juh5w17n6kgf">{t.id}</div>
          <div className="dt-acct" data-id="element-492" data-mp="1jxbr3uyhc9aw">{t.account.substring(0, 14)}</div>
          <div style={{
        fontSize: 8,
        marginTop: 2,
        lineHeight: 1.2
      }} data-id="element-493" data-mp="1iev1nxary6s5">
            <span title={t.name} data-id="element-494" data-mp="1dfih5j7dlbsq">{t.name.substring(0, 16)}</span>
          </div>
          <div style={{
        fontSize: 8,
        color: '#667085',
        marginTop: 2
      }} data-id="element-495" data-mp="1sc9cnhdxi3d2">{t.entry}</div>
          <span className="badge b-blue" style={{
        fontSize: 8
      }} data-id="element-496" data-mp="1s9wqst51v8uq">{t.touchpointType || '—'}</span>
        </div>)}
    </div>;
}
function TouchpointGalleryView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  return <div className="deal-gallery" data-id="element-497" data-mp="1wee90nvrhe3e">
      {touchpoints.map(t => <div key={t.task_id} className="deal-gallery-card" data-id="element-498" data-mp="1wc0d6ct9bxus">
          <div className="dg-id" data-id="element-499" data-mp="2b1fs8vjfeeun">{t.id}</div>
          <div className="dg-title" data-id="element-500" data-mp="263nzy0evcqpx">{t.account}</div>
          <div style={{
        fontSize: 10,
        marginBottom: 4
      }} data-id="element-501" data-mp="264z0zsgxjkdc">
            <span title={t.name} data-id="element-502" data-mp="2k35s8prdim41">{t.name.substring(0, 30)}{t.name.length > 30 ? '…' : ''}</span>
          </div>
          <div style={{
        fontSize: 9,
        color: '#667085',
        marginBottom: 4
      }} data-id="element-503" data-mp="2g2dhfs2us95n">{t.entry}</div>
          <div style={{
        fontSize: 9,
        color: '#344054',
        marginBottom: 4,
        lineHeight: 1.3
      }} data-id="element-504" data-mp="2yqdzjfhka7aq">
            <span title={t.solutionComments} data-id="element-505" data-mp="33oyzddswioyn">{t.solutionComments ? t.solutionComments.substring(0, 50) + (t.solutionComments.length > 50 ? '…' : '') : '—'}</span>
          </div>
          <div className="dg-meta" data-id="element-506" data-mp="2prk8s5qwwb53">
            {t.touchpointType && <span className="badge b-blue" data-id="element-507" data-mp="2unhivubl0l9f">{t.touchpointType}</span>}
            <span className="badge b-gray" data-id="element-508" data-mp="2fzwgy3qg0s33">{t.close_q}</span>
          </div>
          <div className="dg-footer" data-id="element-509" data-mp="2fxthyu7bbom8">
            {t.sub_region} · {t.sc}
          </div>
        </div>)}
    </div>;
}
const TP_TYPE_BUCKETS = ['Internal Alignment', 'Customer Facing', 'No Type'];
function TouchpointWaterfallView({
  touchpoints
}: {
  touchpoints: Touchpoint[];
}) {
  const groups: Record<string, Touchpoint[]> = {};
  for (const b of TP_TYPE_BUCKETS) groups[b] = [];
  for (const t of touchpoints) {
    const bucket = t.touchpointType || 'No Type';
    if (!groups[bucket]) groups[bucket] = [];
    groups[bucket].push(t);
  }
  return <div className="deal-waterfall" data-id="element-510" data-mp="7l7w7duq0gho">
      {TP_TYPE_BUCKETS.map(bucket => {
      const items = groups[bucket] || [];
      return <div key={bucket} className="deal-waterfall-lane" data-id="element-511" data-mp="3uwqsva7k3avj">
            <div className="deal-waterfall-lane-header" data-id="element-512" data-mp="3ux8tsk55yd9s">
              <span data-id="element-513" data-mp="3py601vumvth4">{bucket}</span>
              <span className="lane-count" data-id="element-514" data-mp="3pz7dg6z4gs20">{items.length}</span>
            </div>
            <div className="deal-waterfall-lane-body" data-id="element-515" data-mp="qhhksv390pfx">
              {items.length === 0 && <div style={{
            fontSize: 9,
            color: '#d1d5db',
            textAlign: 'center',
            padding: '12px 4px',
            fontStyle: 'italic'
          }} data-id="element-516" data-mp="mhyxxtrva4ag">none</div>}
              {items.map(t => <div key={t.task_id} className="deal-waterfall-card" data-id="element-517" data-mp="cj9i06jj6twu">
                  <div className="dw-id" data-id="element-518" data-mp="3281z5w8ty7tt">{t.id}</div>
                  <div className="dw-acct" title={t.account} data-id="element-519" data-mp="328uvs4e2rgy8">{t.account.substring(0, 18)}</div>
                  <div style={{
              fontSize: 8,
              marginTop: 2,
              lineHeight: 1.2
            }} data-id="element-520" data-mp="2x8789sp21i8r">
                    <span title={t.name} data-id="element-521" data-mp="2n5tgvex2xm7k">{t.name.substring(0, 20)}</span>
                  </div>
                  <div style={{
              fontSize: 8,
              color: '#667085',
              marginTop: 2
            }} data-id="element-522" data-mp="2s3lhcxbsignd">{t.entry}</div>
                  <div className="dw-tags" data-id="element-523" data-mp="2x2ofryx9lomd">
                    <span className="badge b-blue" style={{
                fontSize: 8
              }} data-id="element-524" data-mp="2x1n01h5b0dyb">{t.touchpointType || '—'}</span>
                  </div>
                </div>)}
            </div>
          </div>;
    })}
    </div>;
}

/* ─── Main panel ───────────────────────────────────────────────────────── */

export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const [dealView, setDealView] = useState<DealViewMode>('cards');
  const [expandedViewMode, setExpandedViewMode] = useState<'deals' | 'touchpoints'>('deals');
  const [scSearch, setScSearch] = useState('');
  const {
    deals,
    touchpoints
  } = useDeals();

  // Touchpoints filters
  const [tpSearchId, setTpSearchId] = useState('');
  const [tpSearchName, setTpSearchName] = useState('');
  const [tpSearchAccount, setTpSearchAccount] = useState('');
  const [tpSearchSc, setTpSearchSc] = useState('');
  const [tpTypeFilter, setTpTypeFilter] = useState<string[]>([]);
  const [tpTypeDropdownOpen, setTpTypeDropdownOpen] = useState(false);
  const [tpEntryFrom, setTpEntryFrom] = useState('');
  const [tpEntryTo, setTpEntryTo] = useState('');
  const tpTypes = [...new Set(touchpoints.map(t => t.touchpointType).filter(Boolean))].sort();
  const parseEntryDate = (entry: string): Date | null => {
    if (!entry) return null;
    const parts = entry.split('/');
    if (parts.length !== 3) return null;
    const [dd, mm, yy] = parts.map(Number);
    const year = yy > 50 ? 1900 + yy : 2000 + yy;
    return new Date(year, mm - 1, dd);
  };
  const touchpointsFiltered = touchpoints.filter(t => {
    if (tpSearchId && !t.id.toLowerCase().includes(tpSearchId.toLowerCase())) return false;
    if (tpSearchName && !t.name.toLowerCase().includes(tpSearchName.toLowerCase())) return false;
    if (tpSearchAccount && !t.account.toLowerCase().includes(tpSearchAccount.toLowerCase())) return false;
    if (tpSearchSc && !t.sc.toLowerCase().includes(tpSearchSc.toLowerCase())) return false;
    if (tpTypeFilter.length > 0 && !tpTypeFilter.includes(t.touchpointType)) return false;
    if (tpEntryFrom || tpEntryTo) {
      const d = parseEntryDate(t.entry);
      if (d) {
        if (tpEntryFrom) {
          const from = parseEntryDate(tpEntryFrom);
          if (from && d < from) return false;
        }
        if (tpEntryTo) {
          const to = parseEntryDate(tpEntryTo);
          if (to && d > to) return false;
        }
      }
    }
    return true;
  });
  const scDataAll = buildSCList(state.r, state.sub, state, deals);
  const tpSCDataAll = buildTouchpointSCList(state.r, state.sub, state, touchpoints);
  const tpSCMap = new Map(tpSCDataAll.map(tp => [tp.n, tp]));
  if (touchpoints.length > 0 && tpSCDataAll.length <= 2) {
    const scSamples = touchpoints.slice(0, 5).map(t => t.sc);
    console.log('[ODPanel] touchpoint sc samples:', scSamples);
    console.log('[ODPanel] unique sc values:', new Set(touchpoints.map(t => t.sc)).size);
  }
  const scFiltered = scSearch ? scDataAll.filter(s => s.n.toLowerCase().includes(scSearch.toLowerCase())) : scDataAll;
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(d => splitSCNames(d.sc))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allForGnarr);
  const maxIP = Math.max(...scFiltered.map(s => s.ip), 1);
  const maxClosed = Math.max(...scFiltered.map(s => s.closed), 1);
  const scColumns: SortableColumn<SCData>[] = [{
    key: 'n',
    label: 'SC Name',
    width: '22%',
    render: p => <span className="name-link" data-id="element-525" data-mp="1jcvit1bxblrg">
          {p.n}
          <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-526" data-mp="1eeakx8k59e66">▾</span>
        </span>
  }, {
    key: 'ip',
    label: 'In-progress',
    width: '14%',
    sortValue: p => p.ip,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-527" data-mp="1ncnn31uab5r0">
            <div className="pf" style={{
          width: `${Math.round(p.ip / maxIP * 100)}%`
        }} data-id="element-528" data-mp="33rxkp0w2m1kd" />
          </div>
          <strong data-id="element-529" data-mp="2ytvqpkx3whu4">{p.ip}</strong>
        </>
  }, {
    key: 'closed',
    label: 'Closed',
    width: '14%',
    sortValue: p => p.closed,
    render: p => <>
          <div className="pbg" style={{
        width: 70
      }} data-id="element-530" data-mp="21esn567mmhyu">
            <div className="pf" style={{
          width: `${Math.round(p.closed / maxClosed * 100)}%`,
          background: '#34d399'
        }} data-id="element-531" data-mp="21dhkj9px3g9b" />
          </div>
          <strong data-id="element-532" data-mp="2afqsbrtswvj0">{p.closed}</strong>
        </>
  }, {
    key: 'avgAge',
    label: 'Avg age (Closed)',
    width: '11%',
    sortValue: p => p.avgAge,
    render: p => <span style={{
      color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
      fontWeight: p.avgAge > 40 ? 600 : 400
    }} data-id="element-533" data-mp="1dndjna445um0">
          {p.avgAge}d
        </span>
  }, {
    key: 'med',
    label: 'Median age (Closed)',
    width: '11%',
    sortValue: p => p.med,
    render: p => <>{p.med}d</>
  }, {
    key: 'gnarr',
    label: 'GNARR (IP)',
    width: '14%',
    sortValue: p => p.gnarr,
    render: p => <>{fmtG(p.gnarr)}</>
  }, {
    key: 'closedGnarr',
    label: 'GNARR (Closed)',
    width: '14%',
    sortValue: p => p.closedGnarr,
    render: p => <>{fmtG(p.closedGnarr)}</>
  }, {
    key: 'tpCount',
    label: 'TP',
    width: '6%',
    sortValue: p => tpSCMap.get(p.n)?.count ?? 0,
    render: p => {
      const count = tpSCMap.get(p.n)?.count ?? 0;
      return <span style={{
        color: count > 0 ? '#3B82F6' : '#8892a0',
        fontWeight: count > 0 ? 600 : 400
      }} data-id="element-534" data-mp="dha0pf0bwgy9">{count}</span>;
    }
  }];
  return <div data-id="element-535" data-mp="8iypp0yas0eg">
      {/* Metrics */}
      <div className="mrow" data-id="element-536" data-mp="8k9riv86my2x">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-537" data-mp="3rqdxy9hw2y4" />
        <MetricCard label="Total Requests / person" value={scFiltered.length ? +(allForGnarr.length / scFiltered.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-538" data-mp="3uwzwx5z2ngy8" />
        <MedianAgeCard ages={ages} data-id="element-539" data-mp="3lxl96eaahkhf" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-540" data-mp="3lwjzoe8x961t" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — requests / touchpoints (click row to expand)" data-id="element-541" data-mp="3gys5teopv9uz" />
      <div className="tw" data-id="element-542" data-mp="1kvdpyfw7muhd">
        <div style={{
        padding: '8px 12px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }} data-id="element-543" data-mp="1kx82p6xbvgad">
          <input type="text" placeholder="Search SC name…" value={scSearch} onChange={e => {
          setScSearch(e.target.value);
          setExpandedSC(null);
        }} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          width: 160,
          color: '#344054',
          background: '#fff'
        }} data-id="element-544" data-mp="1b5aqpxyhwkal" />
        </div>
        <SortableTable columns={scColumns} data={scFiltered} rowKey={p => p.n} defaultSort={{
        key: 'ip',
        dir: 'desc'
      }} onRowClick={p => {
        if (expandedSC === p.n) {
          setExpandedSC(null);
        } else {
          setExpandedSC(p.n);
          setExpandedViewMode('deals');
        }
      }} renderExpandedRow={p => expandedSC === p.n ? <tr key={`exp-${p.n}`} data-id="element-546" data-mp="31kv4a1rxwg7e">
              <td colSpan={8} style={{
          padding: 0
        }} data-id="element-547" data-mp="2woxmrs4xx3t1">
                <div className="exp-inner" data-id="element-548" data-mp="2wmbcvlukuc7n">
                  <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 6
            }} data-id="element-549" data-mp="3aft0bhqdw92y">
                    <div style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#667085',
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                flex: 1
              }} data-id="element-550" data-mp="35okq4jh8cjoq">
                      {expandedViewMode === 'deals' ? <>In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)</> : <>Touchpoints — {p.n} ({tpSCMap.get(p.n)?.count ?? 0} touchpoints)</>}
                    </div>
                    <div style={{
                display: 'flex',
                border: '1px solid #d0d5dd',
                borderRadius: 5,
                overflow: 'hidden'
              }} data-id="element-551" data-mp="3pdldw4cdenei">
                      <button onClick={() => setExpandedViewMode('deals')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  cursor: 'pointer',
                  background: expandedViewMode === 'deals' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'deals' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'deals' ? 600 : 400
                }} data-id="element-552" data-mp="1uysh41zr3qkz">
                        Requests
                      </button>
                      <button onClick={() => setExpandedViewMode('touchpoints')} style={{
                  fontSize: 9,
                  padding: '3px 10px',
                  border: 'none',
                  borderLeft: '1px solid #d0d5dd',
                  cursor: 'pointer',
                  background: expandedViewMode === 'touchpoints' ? '#3B82F6' : '#fff',
                  color: expandedViewMode === 'touchpoints' ? '#fff' : '#344054',
                  fontWeight: expandedViewMode === 'touchpoints' ? 600 : 400
                }} data-id="element-553" data-mp="gv67brds91aw">
                        Touchpoints
                      </button>
                    </div>
                    <ViewSwitcher active={dealView} onChange={setDealView} data-id="element-554" data-mp="2l1r0g30ouncx" />
                  </div>
                  {expandedViewMode === 'deals' ? <>
                      <DealExpandedView deals={p.deals} mode={dealView} data-id="element-555" data-mp="2b65yu5zj7p3p" />
                      {p.closedDeals.length > 0 && <>
                          <div style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#059669',
                  margin: '12px 0 6px',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em'
                }} data-id="element-556" data-mp="33ts6bpbvrdvu">
                            Closed — {p.n} ({p.closed} deals)
                          </div>
                          <DealExpandedView deals={p.closedDeals} mode={dealView} data-id="element-557" data-mp="2u3gpx3b0fc82" />
                        </>}
                    </> : <TouchpointExpandedView touchpoints={tpSCMap.get(p.n)?.touchpoints ?? []} mode={dealView} data-id="element-558" data-mp="3mrmbfs9w158y" />}
                </div>
              </td>
            </tr> : null} data-id="element-545" data-mp="s7823k9v0pdb" />
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-559" data-mp="2m4ejmc4fzywp" />
      <div className="tw" data-id="element-560" data-mp="2m2bnrbglzbk2">
        <div className="sub" data-id="element-561" data-mp="2q58c7isty1vx">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-562" data-mp="3dvu686601lmx">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-563" data-mp="3iu5hmlby0479">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-564" data-mp="3is1dc2a4t36g" />
          </table>
        </div>
      </div>

      {/* Touchpoints */}
      <SectionHeader title="Touchpoints" data-id="element-565" data-mp="39snzyyivzugg" />
      <div style={{
      padding: '8px 18px 0',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap'
    }} data-id="element-566" data-mp="39vk1ewty119n">
        <input type="text" placeholder="Search ID…" value={tpSearchId} onChange={e => setTpSearchId(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 110,
        color: '#344054',
        background: '#fff'
      }} data-id="element-567" data-mp="1yekbu8yzd0jb" />
        <input type="text" placeholder="Search name…" value={tpSearchName} onChange={e => setTpSearchName(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-568" data-mp="1zuy2ax3k1zl7" />
        <input type="text" placeholder="Search account…" value={tpSearchAccount} onChange={e => setTpSearchAccount(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-569" data-mp="1gxn47ftntc7c" />
        <input type="text" placeholder="Search SC…" value={tpSearchSc} onChange={e => setTpSearchSc(e.target.value)} style={{
        fontSize: 10,
        padding: '4px 8px',
        border: '1px solid #d0d5dd',
        borderRadius: 5,
        outline: 'none',
        width: 140,
        color: '#344054',
        background: '#fff'
      }} data-id="element-570" data-mp="3dnwewlfbwnq5" />
        <div style={{
        position: 'relative'
      }} data-id="element-571" data-mp="g2rtvaf9l3j">
          <button onClick={() => setTpTypeDropdownOpen(!tpTypeDropdownOpen)} style={{
          fontSize: 10,
          padding: '4px 8px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 4
        }} data-id="element-572" data-mp="fszw25th3v7">
            {tpTypeFilter.length > 0 ? `Type (${tpTypeFilter.length})` : 'Type ▾'}
          </button>
          {tpTypeDropdownOpen && <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          zIndex: 50,
          background: '#fff',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          boxShadow: '0 2px 6px rgba(0,0,0,.12)',
          minWidth: 160,
          padding: '4px 0'
        }} data-id="element-573" data-mp="278vgm92tztge">
              <div onClick={() => setTpTypeFilter(tpTypeFilter.length === tpTypes.length ? [] : [...tpTypes])} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            color: '#667085',
            borderBottom: '1px solid #eaecf0'
          }} data-id="element-574" data-mp="vo3oc1hnvbyi">
                {tpTypeFilter.length === tpTypes.length ? 'Deselect all' : 'Select all'}
              </div>
              {tpTypes.map(t => <label key={t} style={{
            padding: '4px 10px',
            fontSize: 10,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }} onClick={e => e.stopPropagation()} data-id="element-575" data-mp="1ee34tzzfriqb">
                  <input type="checkbox" checked={tpTypeFilter.includes(t)} onChange={() => {
              setTpTypeFilter(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
            }} data-id="element-576" data-mp="3l75c7gak8vyu" />
                  {t}
                </label>)}
            </div>}
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: '#667085'
      }} data-id="element-577" data-mp="1bargx7lk567c">
          <span data-id="element-578" data-mp="16bwpjh9iy9bm">Entry</span>
          <input type="date" value={tpEntryFrom} onChange={e => setTpEntryFrom(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-579" data-mp="16almjjnwl5lk" />
          <span data-id="element-580" data-mp="1tz4we3mlpqfr">–</span>
          <input type="date" value={tpEntryTo} onChange={e => setTpEntryTo(e.target.value)} style={{
          fontSize: 10,
          padding: '3px 6px',
          border: '1px solid #d0d5dd',
          borderRadius: 5,
          outline: 'none',
          color: '#344054',
          background: '#fff'
        }} data-id="element-581" data-mp="1p25zjdbjz5dv" />
        </div>
      </div>
      <div className="tw" data-id="element-582" data-mp="1ypiej12eonku">
        <SortableTable columns={[{
        key: 'id',
        label: 'ID',
        width: '10%',
        sortValue: (t: Touchpoint) => t.id,
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#8892a0'
        }} data-id="element-584" data-mp="1pvvo7qgywcj1">{t.id}</span>
      }, {
        key: 'name',
        label: 'Touchpoints Name',
        width: '18%',
        sortValue: (t: Touchpoint) => t.name,
        render: (t: Touchpoint) => <span title={t.name} data-id="element-585" data-mp="1g13ks60pevgc">{t.name.substring(0, 24)}</span>
      }, {
        key: 'account',
        label: 'Account',
        width: '14%',
        sortValue: (t: Touchpoint) => t.account,
        render: (t: Touchpoint) => <span title={t.account} data-id="element-586" data-mp="170p11b3e1oey">{t.account.substring(0, 18)}</span>
      }, {
        key: 'sc',
        label: 'SC',
        width: '10%',
        sortValue: (t: Touchpoint) => t.sc,
        render: (t: Touchpoint) => <span title={t.sc} data-id="element-587" data-mp="13w7rc1mbo1jw">{t.sc}</span>
      }, {
        key: 'touchpointType',
        label: 'Type',
        width: '12%',
        sortValue: (t: Touchpoint) => t.touchpointType,
        render: (t: Touchpoint) => t.touchpointType ? <span className="badge b-blue" style={{
          fontSize: 8
        }} data-id="element-588" data-mp="u53eb7g7iqrp">{t.touchpointType}</span> : <span style={{
          color: '#d1d5db'
        }} data-id="element-589" data-mp="p7jza5boq9yp">—</span>
      }, {
        key: 'entry',
        label: 'Entry',
        width: '12%',
        sortValue: (t: Touchpoint) => {
          const p = t.entry.split('/');
          if (p.length !== 3) return 0;
          const [dd, mm, yy] = p.map(Number);
          return new Date(yy > 50 ? 1900 + yy : 2000 + yy, mm - 1, dd).getTime();
        },
        render: (t: Touchpoint) => <span style={{
          fontSize: 9,
          color: '#667085'
        }} data-id="element-590" data-mp="25mcthhsaywn7">{t.entry}</span>
      }, {
        key: 'solutionComments',
        label: 'Comments',
        width: '16%',
        sortValue: (t: Touchpoint) => t.solutionComments,
        render: (t: Touchpoint) => <span title={t.solutionComments} data-id="element-591" data-mp="295282e3pk3ta">{t.solutionComments ? t.solutionComments.substring(0, 35) + (t.solutionComments.length > 35 ? '…' : '') : '—'}</span>
      }]} data={touchpointsFiltered} rowKey={(t: Touchpoint) => t.task_id} defaultSort={{
        key: 'entry',
        dir: 'desc'
      }} data-id="element-583" data-mp="23ov0ljlgdqmd" />
      </div>
    </div>;
}